import {ServiceBase} from '../../ServiceBase';
import {ICmsBannersService} from './cms-banners.service.interface';
import { action, observable, makeObservable } from 'mobx';
import {IBannerDefinition} from './banner-definition/banner-definition.interface';
import {IServiceFactoryExtended} from '../../service-factory-extended.interface';
import {CmsBannerId} from './cm-banner-id/cms-banner-id';
import {Check} from '@sparkware/app-utils';
import {BannerDefinition} from './banner-definition/banner-definition';

import {EmptyBannerDefinition} from './banner-definition/empty-banner-definition';
import {BannerData} from '../cms-api/cms-api.service.interface';

type ReadBannersResponse = Array<{id: CmsBannerId, definition: IBannerDefinition}>;

export class CmsBannersService extends ServiceBase implements ICmsBannersService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this.reaction(
        () => this.services.user.isAuthorized,
        (isAuthorized) => {
            this._reloadBannersContent();
        }
        );
  }


  private _currentUsedBannersIds: {[key: string]: CmsBannerId} = {};

  @observable
  bannersMap: {[key: string]: IBannerDefinition} = {};

  async loadBanner(bannerId: CmsBannerId): Promise<boolean> {
    if(!this.bannersMap[bannerId.key]) {
      let banners = await this._readBanners([bannerId]);
      this._setBanners(banners);
    }
    const hasBannerContent = Check.isObject(this.bannersMap[bannerId.key]);
    return hasBannerContent;
  }

  private _reloadBannersContent() {
    this._readBanners(Object.values(this._currentUsedBannersIds))
        .then(banners => {
          this._setBanners(banners);
        });
  }

  @action.bound
  private _setBanners(banners: ReadBannersResponse) {
    banners.forEach(newBanner => {
      this.bannersMap[newBanner.id.key] = newBanner.definition;
      this._currentUsedBannersIds[newBanner.id.key] = newBanner.id;
    });
  }

    private async _readBanners(bannersIds: CmsBannerId[]): Promise<ReadBannersResponse> {

      let bannersIdsCollection = new BannersIdCollection(bannersIds);

      let cmsResponseData = await this.services.cmsApi.getBanners(bannersIds);
      //create definitions for bannersMap that we received some content
      let bannersWithContent = this._extractBannersWithContent(bannersIdsCollection, cmsResponseData);

      //for banners that we didn't receive any content try to load a default content and then merge the results
      let bannersIdsWithoutContent =  bannersIdsCollection.exclude(bannersWithContent.map(bannerInfo => bannerInfo.id));

      let fallbackBanners = await this._readFallbackBanners(bannersIdsWithoutContent);

      return [...bannersWithContent, ...fallbackBanners];

     /*.catch((err) => {
        let msg = bannersIds.reduce((acc, curr) => acc + '\n' + curr.toString(), '');
        this.services.logger.error(`Failed to load CMS banners for ${msg}`, err);
        return this._readFallbackBanners(bannersIds);
      });
      */
  }

  private _extractBannersWithContent(requestedBannersIdsCollection: BannersIdCollection, cmsResponseData: BannerData[]): ReadBannersResponse {
    if(!Check.isArray(cmsResponseData)) {
      return [];
    }

    return  cmsResponseData
            .filter(bannerData => !Check.isEmpty(bannerData.Content))
            .map(bannerData => {
              let bannerId = requestedBannersIdsCollection.getOriginalBannerId(bannerData.ComponentId, bannerData.TemplateId);
              return {
                id: bannerId,
                definition: new BannerDefinition(bannerData.Content, bannerId)
              };
            });
  }

  private async _readFallbackBanners(bannersIds: CmsBannerId[]): Promise<ReadBannersResponse> {
    if(bannersIds.length === 0) {
      return Promise.resolve([]);
    }
    // for bannersMap that have fallback file we try to read the default banner content and then merge with those without fallback file
    let bannersIdsWithFallbackFile = bannersIds.filter(id => !Check.isEmpty(id.fallbackFileName));
    let bannersIdsGroupedByFallbackFile = this._groupBannersIdsByFallbackFile(bannersIdsWithFallbackFile);
    let fileNames = Object.keys(bannersIdsGroupedByFallbackFile);


    let fallbackData = await this.services.cmsApi.getFallbackBanners(fileNames);

    let result: ReadBannersResponse = [];
    fileNames.forEach(file => {
      let bannerContent = fallbackData[file];
      if(bannerContent) {
        result = result.concat(this._createBannersDefinitionsWithContent(bannersIdsGroupedByFallbackFile[file], bannerContent));
      }
    });

    return result;
  }


  private _groupBannersIdsByFallbackFile(bannersIds: CmsBannerId[]): {[key: string]: CmsBannerId[]} {
    let result = {};
    bannersIds.forEach(bannerId => {
      if(!result[bannerId.fallbackFileName]) {
        result[bannerId.fallbackFileName] = [];
      }

      result[bannerId.fallbackFileName].push(bannerId);
    });

    return result;
  }

  private _createBannersDefinitionsWithContent(bannersIds: CmsBannerId[], bannerContent: string): ReadBannersResponse {
    return bannersIds.map(id => {
      let definition: IBannerDefinition;
      if(Check.isEmpty(bannerContent)) {
        definition = new EmptyBannerDefinition();
      } else {
        definition = new BannerDefinition(bannerContent, id);
      }

      return {id: id, definition: definition};
    });
  }
}


class BannersIdCollection {

  private _originalBannersMap = {};

  constructor(private _originalBannersIds: CmsBannerId[]) {
    this._originalBannersIds.forEach(bannerId => {
      this._originalBannersMap[bannerId.key] = bannerId;
    });
  }

  getOriginalBannerId(componentId: number, templateId: number): CmsBannerId {
    for(let i = 0; i < this._originalBannersIds.length; i++) {
      let bannerId = this._originalBannersIds[i];
      if(bannerId.isMatch(componentId, templateId)) {
        return bannerId;
      }
    }
    
    throw new Error(`Cant't find original banner id for componentId=${componentId} and templateId=${templateId}`);
  }

  exclude(bannersIdsToExclude: CmsBannerId[]): CmsBannerId[] {
    let bannersIdsToExcludeMap = {};
    bannersIdsToExclude.forEach(bannerId => bannersIdsToExcludeMap[bannerId.key] = bannerId);

    return this._originalBannersIds.filter(bannerId => !bannersIdsToExcludeMap[bannerId.key]);
  }
}
